import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Button,
  TextField,
  Slider,
  IconButton,
  Tooltip,
  CircularProgress,
  Stack,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Stop,
  SkipNext,
  SkipPrevious,
  VolumeUp,
  VolumeOff,
  FastForward,
  FastRewind,
  VolumeDown,
} from '@mui/icons-material';

import { api } from '../../services/api';
import ReactModal from '@mui/material/Dialog';
import { Close } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import type { Device } from '../../types';

interface DevicePlaybackControlsProps {
  device: Device;
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
  initialPlaylistPickerOpen?: boolean;
}

const DevicePlaybackControls: React.FC<DevicePlaybackControlsProps> = ({
  device,
  onSuccess,
  onError,
  initialPlaylistPickerOpen = false,
}) => {
  const { t } = useTranslation();

  // State
  const [volume, setVolume] = useState(50);
  const [seekPosition, setSeekPosition] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [playlistPickerOpen, setPlaylistPickerOpen] = useState(initialPlaylistPickerOpen);
  const [searchQuery, setSearchQuery] = useState('');
  const [page] = useState(1);
  const [syncPlayback, setSyncPlayback] = useState(false);
  const [syncStartDelaySeconds, setSyncStartDelaySeconds] = useState(10);

  // Актуальный статус устройства (обновляем каждые 15с)
  const { data: latestDevice } = useQuery({
    queryKey: ['device-latest', device.id],
    queryFn: () => api.getDevice(device.id),
    refetchInterval: 15000,
    staleTime: 0,
    initialData: device,
  });

  const { data: mediaResponse } = useQuery({
    queryKey: ['media-for-device', searchQuery, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), size: '12' });
      if (searchQuery.trim()) params.append('query', searchQuery.trim());
      return api.get(`/media?${params.toString()}`);
    },
    enabled: mediaPickerOpen,
  });

  const { data: playlistsResponse } = useQuery({
    queryKey: ['playlists-for-device'],
    queryFn: async () => api.getPlaylists(undefined, undefined, 1, 100),
    enabled: playlistPickerOpen,
  });

  const hasLoadedPlayback = Boolean(
    latestDevice?.current_media_id ||
    latestDevice?.current_playlist_id ||
    device.current_media_id ||
    device.current_playlist_id,
  );

  useEffect(() => {
    if (initialPlaylistPickerOpen) {
      setPlaylistPickerOpen(true);
    }
  }, [initialPlaylistPickerOpen, device.id]);

  // Mutations для различных команд
  const playMutation = useMutation({
    mutationFn: () => api.sendDeviceCommand(device.id, { command_type: 'play' }),
    onSuccess: () => onSuccess?.(t('devices.commands.play')),
    onError: (error) => onError?.(error.message),
  });

  const pauseMutation = useMutation({
    mutationFn: () => api.sendDeviceCommand(device.id, { command_type: 'pause' }),
    onSuccess: () => onSuccess?.(t('devices.commands.pause')),
    onError: (error) => onError?.(error.message),
  });

  const stopMutation = useMutation({
    mutationFn: () => api.sendDeviceCommand(device.id, { command_type: 'stop' }),
    onSuccess: () => onSuccess?.(t('devices.commands.stop')),
    onError: (error) => onError?.(error.message),
  });

  const nextMutation = useMutation({
    mutationFn: () => api.nextMedia(device.id),
    onSuccess: () => onSuccess?.(t('devices.playback.nextTrack')),
    onError: (error) => onError?.(error.message),
  });

  const previousMutation = useMutation({
    mutationFn: () => api.previousMedia(device.id),
    onSuccess: () => onSuccess?.(t('devices.playback.previousTrack')),
    onError: (error) => onError?.(error.message),
  });

  const seekMutation = useMutation({
    mutationFn: (position: number) => api.seekDevice(device.id, position),
    onSuccess: () => onSuccess?.(t('devices.playback.seekTo')),
    onError: (error) => onError?.(error.message),
  });

  const volumeMutation = useMutation({
    mutationFn: (newVolume: number) => api.setDeviceVolume(device.id, newVolume),
    onSuccess: () => onSuccess?.(t('devices.playback.setVolumeTo')),
    onError: (error) => onError?.(error.message),
  });

  const muteMutation = useMutation({
    mutationFn: (mute: boolean) => api.muteDevice(device.id, mute),
    onSuccess: (_, mute) => {
      setIsMuted(mute);
      onSuccess?.(mute ? t('devices.playback.muteAudio') : t('devices.playback.unmuteAudio'));
    },
    onError: (error) => onError?.(error.message),
  });

  // Команда запуска конкретного медиа
  const playMediaMutation = useMutation({
    mutationFn: (params: { media_id: number; filename?: string; title?: string }) =>
      api.sendDeviceCommand(device.id, {
        command_type: 'play_media',
        command_data: {
          media_id: params.media_id,
          filename: params.filename,
          title: params.title,
          force: true,
          source: 'admin_device_playback_controls',
          // Пусть плеер по умолчанию сам достучится до download URL через /devices/sync
          download_url: `/api/v1/devices/sync/media/${params.media_id}/download`,
        },
      }),
    onSuccess: () => onSuccess?.(t('devices.playback.playSelected')),
    onError: (error: any) => onError?.(error.message || t('common.error')),
  });

  const playPlaylistMutation = useMutation({
    mutationFn: (params: { playlist_id: number; name?: string }) =>
      syncPlayback
        ? api.createPlaybackSyncSession({
            playlist_id: params.playlist_id,
            device_ids: [device.id],
            start_delay_seconds: syncStartDelaySeconds,
          })
        : api.sendDeviceCommand(device.id, {
            command_type: 'play_playlist',
            command_data: {
              playlist_id: params.playlist_id,
              name: params.name,
              force: true,
              source: 'admin_device_playback_controls',
            },
          }),
    onSuccess: (result: any) => {
      if (syncPlayback && result?.start_at) {
        onSuccess?.(`Синхронный запуск запланирован на ${new Date(result.start_at).toLocaleTimeString()}`);
        return;
      }
      onSuccess?.('Команда запуска плейлиста отправлена');
    },
    onError: (error: any) => onError?.(error.message || t('common.error')),
  });

  const handleOpenMediaPicker = () => setMediaPickerOpen(true);
  const handleCloseMediaPicker = () => setMediaPickerOpen(false);
  const handleOpenPlaylistPicker = () => setPlaylistPickerOpen(true);
  const handleClosePlaylistPicker = () => setPlaylistPickerOpen(false);

  const handleMediaPicked = (media: { id: number; filename?: string; title?: string }) => {
    setMediaPickerOpen(false);
    playMediaMutation.mutate({ media_id: media.id, filename: media.filename, title: media.title });
  };

  const handlePlaylistPicked = (playlist: { id: number; name?: string }) => {
    setPlaylistPickerOpen(false);
    playPlaylistMutation.mutate({ playlist_id: playlist.id, name: playlist.name });
  };

  const handleSeek = () => {
    if (seekPosition >= 0) {
      seekMutation.mutate(seekPosition);
    }
  };

  const handleVolumeChange = () => {
    if (volume >= 0 && volume <= 100) {
      volumeMutation.mutate(volume);
    }
  };

  const handleMute = () => {
    muteMutation.mutate(!isMuted);
  };

  const isAnyMutationLoading = 
    playMutation.isPending ||
    pauseMutation.isPending ||
    stopMutation.isPending ||
    nextMutation.isPending ||
    previousMutation.isPending ||
    seekMutation.isPending ||
    volumeMutation.isPending ||
    muteMutation.isPending;
  const isPlaybackStartLoading = isAnyMutationLoading || playMediaMutation.isPending || playPlaylistMutation.isPending;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlayArrow color="primary" />
          {t('devices.playback.title')}
        </Typography>

        {/* Основные кнопки управления */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('devices.actions.basicControls')}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Tooltip title={hasLoadedPlayback ? t('devices.commands.play') : 'Сначала выберите медиа или плейлист'}>
              <span>
                <IconButton
                  color="primary"
                  onClick={() => playMutation.mutate()}
                  disabled={isAnyMutationLoading || !hasLoadedPlayback}
                  size="large"
                >
                  <PlayArrow />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={t('devices.commands.pause')}>
              <span>
                <IconButton
                  color="primary"
                  onClick={() => pauseMutation.mutate()}
                  disabled={isAnyMutationLoading}
                  size="large"
                >
                  <Pause />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={t('devices.commands.stop')}>
              <span>
                <IconButton
                  color="error"
                  onClick={() => stopMutation.mutate()}
                  disabled={isAnyMutationLoading}
                  size="large"
                >
                  <Stop />
                </IconButton>
              </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            {/* Выбрать и запустить медиа */}
            <Tooltip title={t('devices.playback.playMedia')}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenMediaPicker}
                  disabled={isPlaybackStartLoading}
                >
                  {t('devices.playback.playMedia')}
                </Button>
              </span>
            </Tooltip>

            <Tooltip title="Запустить плейлист">
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleOpenPlaylistPicker}
                  disabled={isPlaybackStartLoading}
                >
                  Плейлист
                </Button>
              </span>
            </Tooltip>

            <Tooltip title={t('devices.playback.previousTrack')}>
              <span>
                <IconButton
                  onClick={() => previousMutation.mutate()}
                  disabled={isPlaybackStartLoading}
                  size="large"
                >
                  <SkipPrevious />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={t('devices.playback.nextTrack')}>
              <span>
                <IconButton
                  onClick={() => nextMutation.mutate()}
                  disabled={isPlaybackStartLoading}
                  size="large"
                >
                  <SkipNext />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Продвинутые элементы управления */}
        <Grid container spacing={3}>
          {/* Перемотка */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('devices.playback.seek')}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <FastRewind />
              <TextField
                size="small"
                type="number"
                label={t('devices.playback.position')}
                value={seekPosition}
                onChange={(e) => setSeekPosition(Number(e.target.value))}
                inputProps={{ min: 0 }}
                sx={{ width: '120px' }}
              />
              <Button
                variant="outlined"
                onClick={handleSeek}
                disabled={isAnyMutationLoading}
                startIcon={<FastForward />}
              >
                {t('devices.playback.seekTo')}
              </Button>
            </Stack>
          </Grid>

          {/* Громкость */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('devices.playback.volume')}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <VolumeDown />
              <Slider
                value={volume}
                onChange={(_, value) => setVolume(value as number)}
                min={0}
                max={100}
                step={5}
                valueLabelDisplay="auto"
                sx={{ flexGrow: 1 }}
              />
              <VolumeUp />
              <Button
                variant="outlined"
                onClick={handleVolumeChange}
                disabled={isAnyMutationLoading}
                sx={{ minWidth: '80px' }}
              >
                {volume}%
              </Button>
            </Stack>

            {/* Кнопка Mute */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Tooltip title={isMuted ? t('devices.playback.unmuteAudio') : t('devices.playback.muteAudio')}>
                <span>
                  <IconButton
                    onClick={handleMute}
                    disabled={isAnyMutationLoading}
                    color={isMuted ? 'error' : 'default'}
                  >
                    {isMuted ? <VolumeOff /> : <VolumeUp />}
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
                {isMuted ? t('devices.playback.mute') : t('devices.playback.unmute')}
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        {/* Индикатор загрузки */}
        {isPlaybackStartLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('common.processing')}...
            </Typography>
          </Box>
        )}

        {!hasLoadedPlayback && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Play возобновляет уже загруженный контент. Для запуска выберите медиа или плейлист.
          </Typography>
        )}

        {/* Статус устройства */}
        <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>{t('common.status')}:</strong> {t(`devices.status.${String(latestDevice?.status || device.status).toLowerCase()}`)}
          </Typography>
          {device.current_playlist && (
            <Typography variant="caption" color="text.secondary" display="block">
              <strong>{t('devices.currentPlaylist')}:</strong> {device.current_playlist.name}
            </Typography>
          )}
          {device.current_media && (
            <Typography variant="caption" color="text.secondary" display="block">
              <strong>Сейчас играет:</strong> {device.current_media.title}
            </Typography>
          )}
        </Box>

        {/* Мини-диалог выбора медиа (без вложенных <p> в <p>) */}
        <ReactModal open={mediaPickerOpen} onClose={handleCloseMediaPicker} fullWidth maxWidth="md">
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{t('devices.playback.playMedia')}</Typography>
              <IconButton onClick={handleCloseMediaPicker} size="small"><Close /></IconButton>
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder={t('media.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
              {(mediaResponse?.items || []).map((m: any) => (
                <Box
                  key={m.id}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    border: 1, borderColor: 'divider', borderRadius: 1, p: 1, mb: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body1">{m.title || m.filename}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {m.media_type} • {(m.file_size / (1024*1024)).toFixed(1)} MB
                    </Typography>
                  </Box>
                  <Button variant="contained" size="small" onClick={() => handleMediaPicked({ id: m.id, filename: m.filename, title: m.title })}>
                    {t('devices.playback.playMedia')}
                  </Button>
                </Box>
              ))}
              {!mediaResponse?.items?.length && (
                <Typography variant="body2" color="text.secondary">{t('media.noMedia')}</Typography>
              )}
            </Box>
          </Box>
        </ReactModal>

        <ReactModal open={playlistPickerOpen} onClose={handleClosePlaylistPicker} fullWidth maxWidth="md">
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Запустить плейлист</Typography>
              <IconButton onClick={handleClosePlaylistPicker} size="small"><Close /></IconButton>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
              <FormControlLabel
                control={<Switch checked={syncPlayback} onChange={(event) => setSyncPlayback(event.target.checked)} />}
                label="Синхронно"
              />
              {syncPlayback && (
                <TextField
                  size="small"
                  type="number"
                  label="Старт через, сек"
                  value={syncStartDelaySeconds}
                  onChange={(event) => setSyncStartDelaySeconds(Math.max(3, Number(event.target.value) || 10))}
                  inputProps={{ min: 3, max: 300 }}
                />
              )}
            </Stack>
            <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
              {(playlistsResponse?.items || []).map((playlist: any) => (
                <Box
                  key={playlist.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.25,
                    mb: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body1">{playlist.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {playlist.items_count || 0} элементов
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handlePlaylistPicked({ id: playlist.id, name: playlist.name })}
                  >
                    {syncPlayback ? 'Запустить синхронно' : 'Запустить'}
                  </Button>
                </Box>
              ))}
              {!playlistsResponse?.items?.length && (
                <Typography variant="body2" color="text.secondary">Плейлисты не найдены</Typography>
              )}
            </Box>
          </Box>
        </ReactModal>
      </CardContent>
    </Card>
  );
};

export default DevicePlaybackControls;
